/**
 * Universal file download utility with loading state and native behavior
 */

import { api } from '@/services/http';

export interface DownloadOptions {
  filename?: string;
  showLoading?: boolean;
  onProgress?: (progress: number) => void;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export interface DownloadResult {
  success: boolean;
  error?: Error;
}

export interface ApiDownloadOptions extends DownloadOptions {
  apiEndpoint: string;
}

/**
 * Download file with proper error handling and loading state
 */
export async function downloadFileWithProgress(
  fileUrl: string,
  options: DownloadOptions = {}
): Promise<DownloadResult> {
  const {
    filename,
    showLoading = true,
    onProgress,
    onSuccess,
    onError,
  } = options;

  try {
    // Show loading state if requested
    if (showLoading) {
      onProgress?.(0);
    }

    // Handle blob URLs directly (don't fetch them)
    if (fileUrl.startsWith('blob:')) {
      // Update progress for blob URLs
      onProgress?.(50);

      // For blob URLs, create download link directly
      const url = fileUrl;
      const link = document.createElement('a');

      link.href = url;
      link.download = filename || 'download';
      link.style.display = 'none';

      // Add to DOM, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Update progress to complete
      onProgress?.(100);

      // Clean up blob URL
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);

      onSuccess?.();
      return { success: true };
    }

    // Determine the correct URL for regular URLs
    let downloadUrl = fileUrl;
    let useApi = false;
    if (!fileUrl.startsWith('http') && !fileUrl.startsWith('blob:')) {
      if (fileUrl.startsWith('/')) {
        downloadUrl = fileUrl;
        useApi = true; // Use axios for relative URLs
      } else {
        downloadUrl = fileUrl;
        useApi = true; // Use axios for relative URLs
      }
    }

    // Use axios api for relative URLs, fetch for absolute URLs
    let blob: Blob;
    if (useApi) {
      const response = await api.get(downloadUrl, {
        responseType: 'blob',
        headers: {
          Accept: '*/*',
        },
      });
      blob = response.data;
    } else {
      // Fetch the file as blob for absolute URLs
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          Accept: '*/*',
        },
      });

      if (!response.ok) {
        throw new Error(
          `Download failed: ${response.status} ${response.statusText}`
        );
      }

      blob = await response.blob();
    }

    if (blob.size === 0) {
      throw new Error('Downloaded file is empty');
    }

    // Update progress
    onProgress?.(100);

    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = filename || getFilenameFromUrl(fileUrl) || 'download';
    link.style.display = 'none';

    // Add to DOM, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up URL object
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);

    onSuccess?.();
    return { success: true };
  } catch (error) {
    const downloadError =
      error instanceof Error ? error : new Error('Download failed');
    onError?.(downloadError);
    return { success: false, error: downloadError };
  }
}

/**
 * Extract filename from URL
 */
function getFilenameFromUrl(url: string): string | null {
  try {
    const urlObj = new URL(url, window.location.origin);
    const pathname = urlObj.pathname;
    const filename = pathname.split('/').pop();
    return filename || null;
  } catch {
    return null;
  }
}

/**
 * Check if browser supports download attribute
 */
export function supportsDownload(): boolean {
  const link = document.createElement('a');
  return 'download' in link;
}

/**
 * Fallback download method for older browsers
 */
export async function downloadFileFallback(
  fileUrl: string,
  options: DownloadOptions = {}
): Promise<DownloadResult> {
  const { onError } = options;

  try {
    // Try to open in new window/tab
    const downloadWindow = window.open(fileUrl, '_blank');

    if (!downloadWindow) {
      throw new Error('Popup blocked - unable to download file');
    }

    // Close the window after a short delay
    setTimeout(() => {
      downloadWindow.close();
    }, 2000);

    return { success: true };
  } catch (error) {
    const downloadError =
      error instanceof Error ? error : new Error('Fallback download failed');
    onError?.(downloadError);
    return { success: false, error: downloadError };
  }
}

/**
 * Download file via API endpoint
 */
export async function downloadFileFromApi(
  options: ApiDownloadOptions
): Promise<DownloadResult> {
  const {
    apiEndpoint,
    filename,
    showLoading = true,
    onProgress,
    onSuccess,
    onError,
  } = options;

  try {
    // Show loading state if requested
    if (showLoading) {
      onProgress?.(0);
    }

    // Use axios api to download the file as blob from API endpoint
    const response = await api.get(apiEndpoint, {
      responseType: 'blob',
      headers: {
        Accept: '*/*',
      },
    });

    // Get the blob
    const blob = response.data;

    if (blob.size === 0) {
      throw new Error('Downloaded file is empty');
    }

    // Update progress
    onProgress?.(100);

    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = filename || getFilenameFromUrl(apiEndpoint) || 'download';
    link.style.display = 'none';

    // Add to DOM, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up URL object
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);

    onSuccess?.();
    return { success: true };
  } catch (error) {
    const downloadError =
      error instanceof Error ? error : new Error('Download failed');
    onError?.(downloadError);
    return { success: false, error: downloadError };
  }
}

/**
 * Universal download function that tries modern method first, then fallback
 */
export async function downloadFile(
  fileUrl: string,
  options: DownloadOptions = {}
): Promise<DownloadResult> {
  // Try modern download method first
  const result = await downloadFileWithProgress(fileUrl, options);

  if (!result.success && supportsDownload()) {
    // If modern method failed but browser supports download, try fallback
    return await downloadFileFallback(fileUrl, options);
  }

  return result;
}
